// ✅ src/pages/Inventory/LedgerMainReport_Print.jsx
// React replacement for: LedgerMainReport_print.aspx (Crystal print page)
//
// Old ASPX Crystal params:
//   @suppliercode   <- Session["subcode"]
//   @branchid       <- Session["branchids"]
//   @fromdate       <- Session["str_fromdate"]
//   @todate         <- Session["str_todate"]
//   @vendorType     <- Session["vendertype"]
//
// ✅ This print page:
// - Uses your SAME UI/print template style (like your VehiclewiseReport_Print sample)
// - Reads params from BOTH querystring + navigate state
// - Calls backend to fetch rows and prints HTML table
//
// Expected backend endpoint (choose one):
//   GET /api/ledgerMainReport/print   (recommended)
//   OR GET /api/ledgerMainReport/list (if you already have list)
//
// NOTE: Replace API_BASE if your route differs.

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint, FaSyncAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

// ✅ change if needed
const API_BASE = "http://nissi-india.com/api/ledgerMainReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};
const clean = (v) => (isSelect(v) ? "" : toStr(v));

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

// keep date format as-is (your ASPX used dd/MM/yyyy strings)
const safeDate = (v) => toStr(v);

export default function LedgerMainReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ support BOTH querystring + navigate state
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const passed = location?.state || {};

  // ✅ final filters (match ASPX crystal parameter names as close as possible)
  const filters = useMemo(() => {
    // from URL
    const q = {
      suppliercode: clean(qs.get("suppliercode")),
      branchid: clean(qs.get("branchid")),
      fromdate: safeDate(qs.get("fromdate")),
      todate: safeDate(qs.get("todate")),
      vendorType: clean(qs.get("vendorType")),
      // allow alternates (in case your list page uses different key names)
      subcode: clean(qs.get("subcode")),
      branchids: clean(qs.get("branchids")),
      str_fromdate: safeDate(qs.get("str_fromdate")),
      str_todate: safeDate(qs.get("str_todate")),
      vendertype: clean(qs.get("vendertype")),
    };

    const finalParams = {
      // ✅ primary (Crystal-like)
      suppliercode: q.suppliercode || q.subcode || clean(passed?.suppliercode ?? passed?.subcode),
      branchid: q.branchid || q.branchids || clean(passed?.branchid ?? passed?.branchids),
      fromdate: q.fromdate || q.str_fromdate || safeDate(passed?.fromdate ?? passed?.str_fromdate),
      todate: q.todate || q.str_todate || safeDate(passed?.todate ?? passed?.str_todate),
      vendorType: q.vendorType || q.vendertype || clean(passed?.vendorType ?? passed?.vendertype),

      // optional (for auditing / backend rules if needed)
      loginuserid: Number(sessionStorage.getItem("loginuserid")) || 0,
      branchType: toStr(sessionStorage.getItem("branchtype") || passed?.branchType || ""),
    };

    return finalParams;
  }, [qs, passed]);

  const hasAnyParam = useMemo(() => {
    return (
      !!toStr(filters.suppliercode) ||
      !!toStr(filters.branchid) ||
      !!toStr(filters.fromdate) ||
      !!toStr(filters.todate) ||
      !!toStr(filters.vendorType)
    );
  }, [filters]);

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      if (!hasAnyParam) {
        throw new Error("Print params missing. Open print from Ledger Main Report page.");
      }

      // ✅ pick your endpoint
      // If you already have /list, switch to `${API_BASE}/list`
      const { data } = await axios.get(`${API_BASE}/print`, { params: filters });

      if (!data?.success) throw new Error(data?.message || "Failed to load report");

      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load report";
      setErrMsg(msg);
      setRows([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  /* ---------------- column mapping ----------------
     Your backend rows columns depend on how you return data (Crystal had its own layout).
     Map safely with pick() so it won't break when column names differ.
  */
  const getBillNo = (r) => pick(r, ["billno", "BillNo", "fld_billno", "voucherNo", "VoucherNo"], "-");
  const getDate = (r) => pick(r, ["date", "Date", "fld_date", "voucherDate", "VoucherDate"], "-");
  const getSupplier = (r) => pick(r, ["supplier", "SupplierName", "fld_suppliername", "vendor", "VendorName"], "-");
  const getBranch = (r) => pick(r, ["branch", "BranchName", "fld_branchname", "branchname"], "-");
  const getParticulars = (r) => pick(r, ["particulars", "Particulars", "narration", "Narration", "fld_narration"], "-");
  const getDebit = (r) => pick(r, ["debit", "Debit", "fld_debit", "dr", "DR"], "");
  const getCredit = (r) => pick(r, ["credit", "Credit", "fld_credit", "cr", "CR"], "");
  const getBalance = (r) => pick(r, ["balance", "Balance", "fld_balance"], "");

  return (
    <div className={`${isDark ? "bg-gray-900" : "bg-gray-100"} min-h-screen`}>
      {/* optional wrapper (keep consistent with your project) */}
      <HeaderBar />

      <VehicleLayoutWrapper>
        <div className="w-full bg-white">
          <style>{`
            @page { size: A4 portrait; margin: 10mm; }
            @media print {
              body { background: #fff !important; }
              .no-print { display: none !important; }
              .print-area { margin: 0 !important; padding: 0 !important; }
            }
          `}</style>

          {/* Top bar */}
          <div className="no-print flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="text-sm">
              {loading ? "Loading..." : errMsg ? <span className="text-red-600">{errMsg}</span> : `Records: ${rows.length}`}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
              >
                <FaArrowLeft /> Back
              </button>

              <button
                onClick={load}
                disabled={loading}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-60"
              >
                <FaSyncAlt /> Reload
              </button>

              <button
                onClick={() => window.print()}
                disabled={loading}
                className={`bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60`}
              >
                <FaPrint /> Print
              </button>
            </div>
          </div>

          {/* Print area */}
          <div className="print-area mx-auto max-w-[820px] p-4 print:p-0">
            <div className="border-2 border-black p-3">
              <div className="border border-black">
                {/* Header */}
                <div className="border-b border-black p-2">
                  <div className="flex items-center gap-3">
                    <div className="w-[110px] h-[80px] border border-black flex items-center justify-center">
                      <div className="text-[10px] font-bold text-center leading-3">
                        UNDER
                        <br />
                        CONSTRUCTION
                      </div>
                    </div>

                    <div className="flex-1 text-center leading-5">
                      <div className="font-bold text-lg">Nissi Infotech</div>
                      <div className="font-semibold text-sm">Nissi</div>
                      <div className="font-semibold text-sm">CIVIL CONSTRUCTION</div>
                      <div className="text-[11px] mt-1">
                        3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                      </div>
                      <div className="text-[11px]">
                        Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
                      </div>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="border-b border-black text-center font-bold py-2 text-sm">
                  LEDGER SUPPLIER MAIN REPORT
                </div>

                {/* Filter info */}
                <div className="border-b border-black p-2 text-[12px]">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div>
                        <span className="font-semibold">Supplier Code</span> : {filters.suppliercode || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Branch</span> : {filters.branchid || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Vendor Type</span> : {filters.vendorType || "-"}
                      </div>
                    </div>

                    <div className="text-right">
                      <div>
                        <span className="font-semibold">From</span> : {filters.fromdate || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">To</span> : {filters.todate || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="p-2">
                  <table className="w-full border border-black border-collapse text-[12px]">
                    <thead>
                      <tr>
                        <th className="border border-black px-2 py-1 w-[6%]">S.No</th>
                        <th className="border border-black px-2 py-1 w-[12%]">Bill / Vch No</th>
                        <th className="border border-black px-2 py-1 w-[10%]">Date</th>
                        <th className="border border-black px-2 py-1 w-[16%]">Supplier</th>
                        <th className="border border-black px-2 py-1 w-[12%]">Branch</th>
                        <th className="border border-black px-2 py-1">Particulars</th>
                        <th className="border border-black px-2 py-1 w-[8%]">Debit</th>
                        <th className="border border-black px-2 py-1 w-[8%]">Credit</th>
                        <th className="border border-black px-2 py-1 w-[10%]">Balance</th>
                      </tr>
                    </thead>

                    <tbody>
                      {!loading && rows.length === 0 ? (
                        <tr>
                          <td className="border border-black px-2 py-2 text-center" colSpan={9}>
                            No records found.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, idx) => (
                          <tr key={pick(r, ["id", "fld_id", "RowId"], `${idx}`)}>
                            <td className="border border-black px-2 py-1 text-center">{idx + 1}</td>
                            <td className="border border-black px-2 py-1">{getBillNo(r)}</td>
                            <td className="border border-black px-2 py-1">{getDate(r)}</td>
                            <td className="border border-black px-2 py-1">{getSupplier(r)}</td>
                            <td className="border border-black px-2 py-1">{getBranch(r)}</td>
                            <td className="border border-black px-2 py-1">{getParticulars(r)}</td>
                            <td className="border border-black px-2 py-1 text-right">{getDebit(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getCredit(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getBalance(r) || "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="border-t border-black p-2">
                  <div className="border border-black p-2 text-[11px]">
                    Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
                  </div>
                </div>
              </div>
            </div>

            {/* Debug */}
            <div className="no-print mt-4 text-xs text-gray-600">
              <details>
                <summary className="cursor-pointer">Debug: Query + Filters</summary>
                <pre className="mt-2 p-3 border rounded bg-gray-50 overflow-auto">
                  {JSON.stringify(
                    {
                      urlSearch: location.search,
                      statePassed: passed,
                      filters,
                      rowsCount: rows.length,
                    },
                    null,
                    2
                  )}
                </pre>
              </details>
            </div>
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </VehicleLayoutWrapper>
    </div>
  );
}